// ============================================
// Undo Button — story-screen "rewind last choice" control
// ============================================
//
// In-memory snapshot stack lives on storyManager (see story.js).
// This module owns the topbar button: visibility, badge counter,
// click handler, accessibility state.
//
// Visible whenever ≥1 snapshot is buffered AND no minigame is
// blocking restore. Badge shows snapshot count (1..5). Hidden
// otherwise.

import { events, Events } from '../engine/events.js';
import { storyManager } from '../engine/story.js';

class UndoButtonUI {
    constructor() {
        this._btn = null;
        this._badge = null;
        this._scope = events.createScope('undo-button');
    }

    init() {
        this._btn = document.getElementById('btn-undo');
        if (!this._btn) return;
        this._badge = this._btn.querySelector('.undo-badge');

        this._btn.addEventListener('click', () => this._onClick());

        // Refresh on every choice render — covers both "snapshot pushed
        // (selectChoice)" and "stack cleared on game new/load" paths.
        this._scope.on(Events.STORY_CHOICES_READY, () => this._refresh());
        this._scope.on(Events.UNDO_STACK_CHANGED, () => this._refresh());
        this._scope.on(Events.UNDO_PERFORMED, () => this._refresh());
        this._scope.on(Events.GAME_NEW, () => this._refresh());
        this._scope.on(Events.GAME_LOADED, () => this._refresh());
        this._scope.on(Events.SCREEN_CHANGE, () => this._refresh());

        this._refresh();
    }

    destroy() {
        if (this._scope) { this._scope.destroy(); this._scope = null; }
        this._btn = null;
        this._badge = null;
    }

    _onClick() {
        if (!storyManager || typeof storyManager.canUndo !== 'function') return;
        if (!storyManager.canUndo()) return;
        storyManager.undoLastChoice();
    }

    _refresh() {
        if (!this._btn) return;
        const sm = storyManager;
        const size = (sm && typeof sm.undoStackSize === 'function') ? sm.undoStackSize() : 0;
        const canUndo = (sm && typeof sm.canUndo === 'function') ? sm.canUndo() : false;

        if (size > 0 && canUndo) {
            this._btn.classList.remove('hidden');
            this._btn.disabled = false;
        } else {
            this._btn.classList.add('hidden');
            this._btn.disabled = true;
        }

        if (this._badge) this._badge.textContent = String(size);
    }
}

export const undoButtonUI = new UndoButtonUI();

if (typeof window !== 'undefined') {
    window._undoButtonUI = undoButtonUI;
}
